'use client';

import { formatAddress } from '@/utils/formatAddress';
import { faBuildingColumns } from '@fortawesome/pro-duotone-svg-icons';
import { scrambleText } from '@/utils/scrambleText';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import { useHasTenderAccess } from '@/hooks/useHasTenderAccess';
import { Contractor } from '@/actions/dtos/tender/contractor';
import React, { useEffect, useState } from 'react';
import { RoundedSectionCard } from '@/components/common/RoundedSectionCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TenderAccessButton from '@/components/tender/access/TenderAccessButton';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { useAccount } from '@/hooks/useAccount';
import { useSelectText } from '@/hooks/useSelectText';

interface ContractorDetailsSectionProps {
  tenderId: string;
  contractor: Contractor;
  isActiveTender: boolean;
}

const ContractorDetailsSection: React.FC<ContractorDetailsSectionProps> = ({
  tenderId,
  contractor,
  isActiveTender,
}) => {
  const { isAuthenticated, isLoading: isAuthenticationLoading } = useAuthenticated();
  const formattedAddress = formatAddress(contractor.authority.address);
  const addressLines = formattedAddress ? formattedAddress.split(', ') : ['-'];
  const { currentTier, loading: isAccountLoading } = useAccount();
  const { tenderAccess, hasAccessPermission, isLoading: isAccessLoading } = useHasTenderAccess(tenderId);
  const isBasicTier = currentTier === SubscriptionTier.BASIC;
  const [shouldHideInformation, setShouldHideInformation] = useState<boolean>(
    !isAuthenticated || (isBasicTier && !tenderAccess),
  );
  const isLoading = isAuthenticationLoading || isAccountLoading || isAccessLoading;
  const [showAccessButton, setShowAccessButton] = useState(
    isActiveTender && !isLoading && !tenderAccess && (isBasicTier || !isAuthenticated),
  );
  const selectTextClass = useSelectText();

  useEffect(() => {
    setShouldHideInformation(!isAuthenticated || (isBasicTier && !tenderAccess));
  }, [isAuthenticated, isBasicTier, tenderAccess]);

  useEffect(() => {
    setShowAccessButton(isActiveTender && !isLoading && !tenderAccess && (isBasicTier || !isAuthenticated));
  }, [isActiveTender, isLoading, tenderAccess, isBasicTier, isAuthenticated]);

  if (isLoading) {
    return null;
  }

  const details = [
    {
      label: 'Name',
      data: scrambleText(contractor.authority.name, shouldHideInformation),
    },
    {
      label: 'Adresse',
      data: (
        <div className="flex flex-col">
          {addressLines.map((line, index) => (
            <span key={index}>{scrambleText(line, shouldHideInformation)}</span>
          ))}
        </div>
      ),
    },
    {
      label: 'Website',
      data: scrambleText(contractor.authority.website || '-', shouldHideInformation),
    },
    {
      label: 'Kontakt',
      data: (
        <div className="flex flex-col">
          {contractor.contact?.email ||
          contractor.contact?.phone ||
          contractor.contact?.fax ||
          contractor.contact?.name ? (
            <>
              {contractor.contact.name && <span>{scrambleText(contractor.contact.name, shouldHideInformation)}</span>}
              {contractor.contact.email && <span>{scrambleText(contractor.contact.email, shouldHideInformation)}</span>}
              {contractor.contact.phone && <span>{scrambleText(contractor.contact.phone, shouldHideInformation)}</span>}
              {contractor.contact.fax && (
                <span>Fax: {scrambleText(contractor.contact.fax, shouldHideInformation)}</span>
              )}
            </>
          ) : (
            <span>{scrambleText('-', shouldHideInformation)}</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <RoundedSectionCard>
      <div className="text-primary">
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-7 w-6 items-center justify-center">
              <FontAwesomeIcon className="fa-fw h-6 w-6" icon={faBuildingColumns} />
            </span>
            <h2 className="text-xl font-light">AUFTRAGGEBER</h2>
          </div>
          {showAccessButton && (
            <div className="max-sm:hidden">
              <TenderAccessButton
                tenderId={tenderId}
                isAuthenticated={isAuthenticated}
                hasAccess={hasAccessPermission}
                label={'Freischalten'}
              />
            </div>
          )}
        </div>
        <div className="grid max-xl:grid-cols-2 max-md:grid-cols-1 lg:grid-cols-4">
          {details.map((item, index) => (
            <div key={index} className="text-center">
              <div className="line-clamp-1 text-xl font-light">{item.label}</div>
              <hr className="h-0.5 bg-[#939BA4]" />
              <div
                className={`${selectTextClass} overflow-hidden text-ellipsis pb-6 pt-2 text-base font-normal xl:mb-1`}
              >
                {shouldHideInformation ? (
                  <div className={`${selectTextClass} blur-sm filter`}>{item.data}</div>
                ) : (
                  <div>{item.data}</div>
                )}
              </div>
            </div>
          ))}
        </div>
        {showAccessButton && (
          <div className="sm:hidden">
            <TenderAccessButton
              tenderId={tenderId}
              isAuthenticated={isAuthenticated}
              hasAccess={hasAccessPermission}
              label={'Freischalten'}
            />
          </div>
        )}
      </div>
    </RoundedSectionCard>
  );
};

export default ContractorDetailsSection;
